/**
 * WHOSE WORDS ARE ON THE PRODUCT PAGE — the shop's, or ours.
 *
 * The server walks variant → product → category → business and publishes what
 * it found plus WHICH LINK answered. When no row in the shop said anything it
 * answers `level: 'platform'` with a null value, because the words are not the
 * server's to choose: it does not know whether this shopper reads Hebrew,
 * Arabic or English. They live in the locale files beside every other string a
 * shopper reads, and this module is where the two meet.
 *
 * ── WHAT THE DEFAULT IS ALLOWED TO SAY ──────────────────────────────────────
 *
 * It is a legal statement made on a merchant's behalf, so it asserts nothing
 * commercial: no warranty period, no return window, no permission the merchant
 * never gave. It states the statutory position — true whether or not anybody
 * typed it — and points at the shop for the specifics. That is why
 * `platformDefault` carries a blurb and never `months`, `allowed` or
 * `windowDays`, and why the returns panel prints no summary claim above it.
 *
 * ── AND IT IS NEVER SILENTLY THEIRS ─────────────────────────────────────────
 *
 * Nothing here writes. `isDefault` rides along so the portal can say out loud
 * that these are our words and not theirs — a default that quietly becomes a
 * merchant's saved text stops improving when we improve it, and they can no
 * longer tell what they actually agreed to.
 */
export const PLATFORM_LEVEL = 'platform';

/**
 * Which grammatical number a count needs — the same rule the server's
 * `shop-policy/warranty-sentence.ts` applies when it picks the sentence key.
 * Hebrew has a dual (שנה / שנתיים) and Arabic splits again at three and at
 * eleven; "1 שנים" on a product page is how a generated sentence gives itself
 * away. Kept here for the length CHIP, which the server does not pick a key
 * for because it is not a sentence.
 */
export function countCategory(n) {
  if (n === 1) return 'one';
  if (n === 2) return 'two';
  if (n >= 3 && n <= 10) return 'few';
  return 'other';
}

/** Words a shopper could read, once the subset's markup is off. */
export function hasProse(blurb) {
  return typeof blurb === 'string' && blurb.replace(/<[^>]*>/g, '').trim() !== '';
}

/**
 * The warranty this page should print, or null for no tab at all.
 *
 * Three ways to get null, and every one is the shop's own answer rather than
 * an accident: they said NO, they said nothing, or the server is too old to
 * say. The owner's rule is unchanged — "if there's no warranty, don't have
 * this tab at all in the product" — and it now has a real answer behind it.
 *
 * There is deliberately no fixed warranty boilerplate any more. A sentence
 * built from the shop's own yes-and-how-long beats one we wrote for everybody,
 * and asserting a warranty position for a merchant who never answered is not
 * ours to do.
 */
export function warrantyForDisplay(warranty, sentence, t) {
  if (!warranty || warranty.covered === false) return null;
  // Their own words replace the generated sentence entirely.
  if (hasProse(warranty.blurb)) {
    return { duration: durationOf(warranty), blurb: warranty.blurb, isGenerated: false };
  }
  if (!sentence || !sentence.key) return null;
  return {
    duration: durationOf(warranty),
    blurb: t(sentence.key, { ...(sentence.params || {}), defaultValue: '' }),
    isGenerated: true,
  };
}

/** The length as a chip, when the shop measured it. Never invented. */
function durationOf(warranty) {
  const n = Number(warranty && warranty.durationValue);
  if (!Number.isInteger(n) || n <= 0) return null;
  return { value: n, unit: warranty.durationUnit || 'months' };
}

/** Today's default, and the one a food shop gets (store-wide polish, Task 4). */
export const PLATFORM_DEFAULT_RETURNS_KEY = 'productDetail.returnPolicy.platformDefault';
export const PLATFORM_DEFAULT_RETURNS_FOOD_KEY = 'productDetail.returnPolicy.platformDefaultFood';

/**
 * Whether a returns row SAYS anything: a refusal, a window, or words. An empty
 * row (`{ allowed: true }`, the DTO minimum) says nothing. See below.
 */
export function statesReturns(policy) {
  return !!policy && (policy.allowed === false || !!policy.windowDays || hasProse(policy.blurb));
}

/**
 * The returns promise this page should print, or null for no section.
 *
 * ── AN EMPTY PROMISE IS SILENCE, NOT A REFUSAL TO SPEAK ────────────────────
 *
 * `{ allowed: true }` is the MINIMAL shape the API's own DTO documents — every
 * other field on it is optional — and it is exactly what a portal field left
 * untouched produces. It used to erase the section entirely: a shop that said
 * "returns: yes" and nothing else rendered LESS than a shop that said nothing
 * at all, which still got the statutory paragraph. Asserting the promise was
 * strictly worse than staying quiet.
 *
 * A row with nothing sayable in it says nothing, so it is answered the way
 * nothing is answered: with the platform default. The distinction that
 * survives is a different one — whether the SERVER named a level at all. A
 * payload from before `returnPolicyLevel` existed carries none, and inventing
 * a default from a missing field would put our words on a page whose shop may
 * well have its own, three fields away, with nobody the wiser.
 *
 * `allowed: false` is not empty. A refusal is a statement a shopper needs, and
 * it is never overwritten by the default.
 *
 * ── A FOOD SHOP'S DEFAULT NAMES THE EXCEPTION ──────────────────────────────
 *
 * Food, perishable goods and goods made to order are not covered by the right
 * to cancel a purchase made at a distance. So for a shop that says it sells
 * food (`salon.sellsFood`, set on My Page → Shop), the default says so, in
 * the wording the owner approved. It still claims no window and no refusal.
 * The shop's own statement still wins. `sellsFood` is read strictly: only
 * `true` is a yes.
 */
export function returnsForDisplay(policy, level, t, { sellsFood = false } = {}) {
  if (statesReturns(policy)) {
    return {
      allowed: policy.allowed !== false,
      windowDays: policy.windowDays ?? null,
      blurb: hasProse(policy.blurb) ? policy.blurb : null,
      isDefault: false,
    };
  }
  // No level named ⇒ the server is too old to say which link answered, and a
  // default asserted on that basis would be a guess wearing our name. That
  // holds for a food shop too: the checkout, whose cards carry no level, says
  // food's exception its own way (`checkoutConfidence.basketPromise`).
  if (typeof level !== 'string' || level === '') return null;
  const food = sellsFood === true;
  return {
    // `allowed: null` — not true, not false. A summary line reading "Returns
    // accepted" would be us promising on a merchant's behalf; "Final sale"
    // would be us refusing on their behalf. The panel prints neither.
    allowed: null,
    windowDays: null,
    blurb: t(food ? PLATFORM_DEFAULT_RETURNS_FOOD_KEY : PLATFORM_DEFAULT_RETURNS_KEY, { defaultValue: '' }),
    isDefault: true,
  };
}

/**
 * THE ONE-LINE SUMMARY OF A RESOLVED RETURNS PROMISE — or null.
 *
 * Lifted out of the product page's returns panel so that the CHECKOUT prints
 * the same sentence, from the same keys, for the same resolved promise. Two
 * screens describing one policy in two different sentences is the defect this
 * exists to make impossible: a shopper who read "Returns accepted within 14
 * days" on the product and "Easy returns" at the till has been told two
 * things, and only one of them is the merchant's.
 *
 * `allowed === null` is the platform default and prints NOTHING: "Returns
 * accepted" would be us promising on a merchant's behalf, "Final sale" would
 * be us refusing on theirs. The statutory paragraph beneath it on the product
 * page claims neither.
 */
export function returnsSummaryLine(shown, t) {
  if (!shown || shown.allowed === null) return null;
  if (!shown.allowed) {
    return t('productDetail.returnPolicy.notAllowed', { defaultValue: 'Final sale — no returns or exchanges' });
  }
  return shown.windowDays
    ? t('productDetail.returnPolicy.allowedWithWindow', { defaultValue: 'Returns accepted within {{n}} days', n: shown.windowDays })
    : t('productDetail.returnPolicy.allowed', { defaultValue: 'Returns accepted' });
}

/**
 * The warranty's LENGTH as its own short label, or null when the shop
 * measured none. Shared with the checkout for the reason above; the grammar
 * rule is `countCategory`, because "1 שנים" gives a generated sentence away.
 */
export function warrantyLengthLabel(duration, t) {
  if (!duration) return null;
  return t(`productDetail.warranty.length.${duration.unit}.${countCategory(duration.value)}`, {
    defaultValue: '{{n}} {{unit}}',
    n: duration.value,
    unit: duration.unit,
  });
}
